import { useEffect, useRef, useState } from "react";
import { throttle } from "lodash";
import $ from "jquery";
import Modal from "./shared/Modal";
import { csrfTokenHeader } from "../helpers/reactQueryApi";
import { navigateAfterLegalConfirmation } from "../helpers/legalNavigation";
import Bridge from "../turbo/bridge";

// Replacement for the legacy Backbone SessionWatchdog (removed from
// _application.js.coffee). The legacy one only reacted to jQuery $.ajax via
// `ajaxComplete`, so it was blind to the modern fetch/React-Query layer: a
// failed save on an expired session / stale CSRF token (POST /activities -> 422)
// showed only a generic error toast with no prompt to sign in or reload. See #4982.
//
// It periodically (and reactively, after any failed request — including fetch)
// asks /system/session whether the session is still valid, and surfaces a
// blocking modal prompting the user to sign in again or reload when it isn't.

type SessionStatus = {
  signed_in: boolean;
  current_membership_id?: string;
  xsrf_token_valid?: boolean;
  acknowledged_legal_checkboxes?: boolean;
};

type WatchdogModal = "signed_out" | "reload_required";

const SESSION_URL = "/system/session";
// Recheck interval while the legal modal/alert is pending (self-healing).
const LEGAL_RECHECK = 15_000;

// "Are we on the confirmation page?" — inside the native app /legal renders
// as a Turbo snapshot while the real webview URL stays on the OLD page. The
// pathname lies there, so the form in the DOM is the reliable signal. Only a
// VISIBLE form counts: after the SPA recovery it stays behind hidden inside
// turbo-root (offsetParent is null then).
const onLegalPage = () => {
  if (window.location.pathname === "/legal") return true;
  const form = document.querySelector<HTMLFormElement>(
    'form[data-controller~="shared--legal-checkboxes"]',
  );
  return !!form && form.offsetParent !== null;
};
const CHECK_THROTTLE = 60_000;
// Extra grace period on top of the server-side timeout before we check.
const SCHEDULE_GRACE = 5_000;
// Inside the native app also poll on a steady schedule: there are no tab
// reloads and no reliable focus events, so changed legal texts would only
// surface on the next manual reload otherwise (#104).
const APP_POLL = 60_000;
// The native alert unavoidably has a cancel button in existing app versions —
// after a cancel, show it again at most this much later.
const APP_ALERT_REPEAT = 60_000;
// Alert mutex via a short-lived cookie: native dialogs are activity-wide — if
// several tab webviews post one, they stack on top of each other on Android.
// Cookies are the only thing the app webviews reliably share (localStorage is
// not enabled in the Android WebView, domStorageEnabled is never set there).
const APP_ALERT_MUTEX_COOKIE = "tixxt_legal_alert_at";
// The mutex belongs to the webview that alerted first: only it may follow up —
// a purely time-based mutex would let the other webviews stack further dialogs
// on top while the first one sits unanswered. If the owner goes quiet for this
// long it counts as gone (webview reloaded) and the mutex may be taken over.
const APP_ALERT_STEAL = 180_000;
const ALERT_OWNER_ID = Math.random().toString(36).slice(2);

const readAlertMutex = () => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${APP_ALERT_MUTEX_COOKIE}=(\\w+)\\.(\\d+)`),
  );
  return match
    ? { owner: match[1], at: Number(match[2]) }
    : { owner: null, at: 0 };
};

const writeAlertMutex = () => {
  document.cookie = `${APP_ALERT_MUTEX_COOKIE}=${ALERT_OWNER_ID}.${Date.now()}; path=/; max-age=600; SameSite=Lax`;
};

const clearAlertMutex = () => {
  document.cookie = `${APP_ALERT_MUTEX_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

// Reload broadcast: the OK of the (activity-wide) alert only runs inside the
// webview that posted it — which may be a background tab. This cookie tells
// the other unacknowledged webviews about the OK so they reload as well.
const APP_RELOAD_COOKIE = "tixxt_legal_reload_at";

const readReloadRequest = () => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${APP_RELOAD_COOKIE}=(\\d+)`),
  );
  return match ? Number(match[1]) : 0;
};

const writeReloadRequest = () => {
  document.cookie = `${APP_RELOAD_COOKIE}=${Date.now()}; path=/; max-age=30; SameSite=Lax`;
};

// Only broadcasts NEWER than this document count: a freshly (re)loaded page
// must never react to the broadcast that caused its own load — on pages that
// skip the legal gate (imprint, super user, …) that would reload in a loop
// for as long as the cookie lives.
const reloadRequested = () => readReloadRequest() > performance.timeOrigin;

// Visible loading feedback for the reload after the alert OK: a full document
// load otherwise shows plain nothing for a few seconds inside the app.
const reloadWithOverlay = () => {
  if (!document.getElementById("legal-reload-overlay")) {
    const overlay = document.createElement("div");
    overlay.id = "legal-reload-overlay";
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:99999;background:rgba(255,255,255,0.75);display:flex;align-items:center;justify-content:center;";
    overlay.innerHTML =
      "<style>@keyframes tixxt-legal-spin{to{transform:rotate(360deg)}}</style>" +
      '<div style="width:32px;height:32px;border:3px solid #d1d5db;border-top-color:#4b5563;border-radius:50%;animation:tixxt-legal-spin 0.8s linear infinite"></div>';
    document.body.appendChild(overlay);
  }
  window.location.reload();
};

// Visibility probe: requestAnimationFrame does not fire in webviews that are
// not being rendered (background tabs) — document.hidden is not reliable
// there (Android reports background tabs as visible).
const probeVisibility = () =>
  new Promise<boolean>((resolve) => {
    let painted = false;
    requestAnimationFrame(() => {
      painted = true;
      resolve(true);
    });
    setTimeout(() => {
      if (!painted) resolve(false);
    }, 500);
  });

export default function SessionWatchdog() {
  const [modal, setModal] = useState<WatchdogModal | null>(null);

  // Refs so the long-lived listeners/timers always see current values without
  // re-subscribing on every render.
  const stoppedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  // Modal is open because legal texts are unacknowledged — keep polling and
  // resolve on its own once another tab/the app confirmed.
  const legalPendingRef = useRef(false);
  // When the native app alert was last shown (follow-up after a cancel).
  const lastLegalAlertAtRef = useRef(0);
  // An alert is already scheduled for display (the short delay is running).
  const pendingLegalAlertRef = useRef(false);
  const legalAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fast watcher for the reload broadcast (only while unacknowledged).
  const reloadWatchRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const sessionTimeoutIn = (window.Settings?.sessionTimeoutIn ?? 0) * 1000;
    // Without a configured timeout we cannot meaningfully schedule checks.
    if (!sessionTimeoutIn) return;

    const stop = (next: WatchdogModal) => {
      stoppedRef.current = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      checkSession.cancel();

      // Inside the native app we can't show a useful sign-in modal; reload to
      // let the app shell handle re-authentication (matches legacy behaviour).
      if (next === "signed_out" && window.isApp) {
        window.location.reload();
        return;
      }
      setModal(next);
    };

    const scheduleCheck = () => {
      if (stoppedRef.current) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(
        () => void checkSession(),
        sessionTimeoutIn + SCHEDULE_GRACE,
      );
    };

    // The actual check. The in-flight guard keeps concurrent triggers (e.g.
    // rapid focus/visibility toggling) from stacking parallel session requests.
    const runCheck = async () => {
      if (stoppedRef.current || inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        let response: Response;
        try {
          // Raw fetch (not fetchApi) so this request does not itself emit
          // "tixxt:request-complete" and loop back into the watchdog. The
          // X-CSRF-Token header is required for the server's xsrf_token_valid
          // check (legacy rails-ujs added it to $.ajax automatically).
          response = await fetch(SESSION_URL, {
            credentials: "include",
            headers: { Accept: "application/json", ...csrfTokenHeader() },
          });
        } catch {
          // Offline / network hiccup — don't punish the user, just retry later.
          window.debug?.("SessionWatchdog: session check failed (offline?)");
          return;
        }

        if (response.status === 401) return stop("signed_out");
        if (!response.ok) return;

        const data = (await response.json()) as SessionStatus;

        if (!data.signed_in) return stop("signed_out");
        if (data.current_membership_id !== Preload.current_member?.id)
          return stop("reload_required");
        if (!data.xsrf_token_valid) return stop("reload_required");
        // /legal is where the user acknowledges the checkboxes in the first
        // place, so a pending acknowledgement there is expected, not a change.
        if (!data.acknowledged_legal_checkboxes && !onLegalPage()) {
          // In the app's sign-in flow (login webview on /users/…) go straight
          // to the confirmation page — a "reload the page" prompt makes no
          // sense there. Without return_to: going back to the sign-in page
          // would be no useful return target, the start page applies.
          if (window.isApp && window.location.pathname.startsWith("/users/")) {
            window.location.assign("/legal");
            return;
          }
          if (window.isApp) {
            // Pick up the reload broadcast: the alert OK may have arrived in
            // another webview. Between the regular rechecks also look every
            // 500ms (a plain cookie read, no request).
            if (reloadRequested()) {
              reloadWithOverlay();
              return;
            }
            if (!reloadWatchRef.current) {
              reloadWatchRef.current = setInterval(() => {
                if (reloadRequested()) reloadWithOverlay();
              }, 500);
            }
          }
          // A native alert inside the app: the web modal would not block the
          // native tab bar and would confusingly stay around on tab switches.
          // OK fully reloads the page — the legal gate then redirects to the
          // confirmation page including ?return_to (same view, no push).
          // The cancel button cannot be hidden in existing app versions; the
          // alert simply comes back after APP_ALERT_REPEAT then.
          if (window.isApp) {
            if (
              !pendingLegalAlertRef.current &&
              Date.now() - lastLegalAlertAtRef.current > APP_ALERT_REPEAT
            ) {
              pendingLegalAlertRef.current = true;
              // Wait briefly instead of alerting right away: whatever
              // triggered this check (e.g. a native tab reload) may already
              // have started a navigation through the legal gate — the
              // document is about to go away or already shows the
              // confirmation page, and the alert would only compete with it
              // for the screen.
              legalAlertTimerRef.current = setTimeout(
                () => {
                  void (async () => {
                    pendingLegalAlertRef.current = false;
                    // Only the visible tab alerts: the watchdogs of ALL tab
                    // webviews keep polling — hidden ones catch up when they
                    // become visible (or have healed themselves by then).
                    if (stoppedRef.current || document.hidden || onLegalPage())
                      return;
                    if (!(await probeVisibility())) return;
                    // Mutex: only the owner alerts; it is only free when
                    // there is none yet or the owner has gone quiet for
                    // APP_ALERT_STEAL.
                    const mutex = readAlertMutex();
                    const mine = mutex.owner === ALERT_OWNER_ID;
                    const free =
                      !mutex.owner || Date.now() - mutex.at > APP_ALERT_STEAL;
                    if (!mine && !free) return;
                    writeAlertMutex();
                    lastLegalAlertAtRef.current = Date.now();
                    Bridge.alert(
                      I18n.t("js.session_modal.reload_required.title"),
                      I18n.t("js.session_modal.reload_required.body"),
                      "legalCheckboxesChanged",
                      () => {
                        writeReloadRequest();
                        reloadWithOverlay();
                      },
                    );
                  })();
                  // Jitter against ties: if several webviews check the mutex
                  // at the same moment they all alert — slightly staggered,
                  // the second one sees the first one's entry.
                },
                3_000 + Math.random() * 3_000,
              );
            }
            // Keep polling: if the confirmation happens in another tab in
            // the meantime, the state resolves on its own.
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(
              () => void runCheck(),
              LEGAL_RECHECK,
            );
            return;
          }
          // Show the modal but KEEP polling: once another tab confirmed, the
          // modal resolves itself via reload — instead of requiring a manual
          // reload everywhere.
          legalPendingRef.current = true;
          setModal("reload_required");
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => void runCheck(), LEGAL_RECHECK);
          return;
        }
        if (data.acknowledged_legal_checkboxes) {
          lastLegalAlertAtRef.current = 0;
          clearAlertMutex();
          if (reloadWatchRef.current) {
            clearInterval(reloadWatchRef.current);
            reloadWatchRef.current = null;
          }
          // Self-healing: the acknowledgement arrived (from another tab/the
          // app).
          if (legalPendingRef.current) {
            window.location.reload();
            return;
          }
          // Send app tabs parked on /legal automatically back to the page
          // the gate redirected them away from.
          if (window.isApp && onLegalPage()) {
            navigateAfterLegalConfirmation();
            return;
          }
        }
      } finally {
        inFlightRef.current = false;
      }
    };

    // Throttled variant for the noisy per-request path. Resume triggers
    // (focus/visibility) call runCheck directly to bypass the throttle.
    const checkSession = throttle(runCheck, CHECK_THROTTLE);

    // A completed request tells us something about the session: a success
    // extended its lifetime (push the check out), a failure might mean it's
    // gone — likely an expired session / stale CSRF token (check now).
    const onRequestObserved = (status: number, url: string) => {
      if (String(url).indexOf(SESSION_URL) === 0) return;
      if (status <= 399) scheduleCheck();
      else void checkSession();
    };

    const onRequestComplete = (event: Event) => {
      const detail = (event as CustomEvent<{ status: number; url: string }>)
        .detail;
      if (detail) onRequestObserved(detail.status, detail.url);
    };

    // Re-check whenever the user comes back: visibilitychange covers tab
    // switches, focus covers returning from another window/app. Bypass the
    // throttle so a return-to-tab checks immediately even within the 60s
    // window; the in-flight guard dedupes when both events fire together.
    const onResume = () => void runCheck();
    const onVisibilityChange = () => {
      if (document.hidden) scheduleCheck();
      else onResume();
    };

    window.addEventListener("tixxt:request-complete", onRequestComplete);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onResume);

    // Legacy jQuery/Turbo (jquery_ujs) requests don't go through fetchApi, so
    // also listen to jQuery's ajaxComplete (namespaced for a clean teardown).
    $(document).on("ajaxComplete.sessionWatchdog", (_event, jqXHR, settings) =>
      onRequestObserved(jqXHR.status, String(settings?.url)),
    );

    scheduleCheck();

    // A standalone interval instead of scheduleCheck: that one is reset by
    // every successful request (session-extension semantics), but the app
    // fires background requests continuously — the timer would never expire
    // there and changed legal texts would go unnoticed.
    const appPollInterval = window.isApp
      ? setInterval(() => void runCheck(), APP_POLL)
      : null;

    return () => {
      window.removeEventListener("tixxt:request-complete", onRequestComplete);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onResume);
      $(document).off("ajaxComplete.sessionWatchdog");
      if (appPollInterval) clearInterval(appPollInterval);
      if (reloadWatchRef.current) clearInterval(reloadWatchRef.current);
      if (legalAlertTimerRef.current) clearTimeout(legalAlertTimerRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      checkSession.cancel();
    };
  }, []);

  if (!modal) return null;

  const kind =
    modal === "signed_out" ? "inactivity_sign_out" : "reload_required";

  return (
    <Modal title={I18n.t(`js.session_modal.${kind}.title`)} static>
      <p>{I18n.t(`js.session_modal.${kind}.body`)}</p>
      <p>
        {modal === "signed_out" ? (
          <a href="/users/sign_in" className="btn btn-primary">
            {I18n.t("js.session_modal.actions.to_sign_in")}
          </a>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            {I18n.t("js.session_modal.actions.reload")}
          </button>
        )}
      </p>
    </Modal>
  );
}
